import { Link } from 'react-router-dom';
import { BrandBanner } from '../components/BrandBanner';
import { Container } from '../components/Container';
import { CTAButton } from '../components/CTAButton';
import { ProgramCard } from '../components/ProgramCard';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { programs } from '../lib/programs';
import { useDocumentMeta } from '../lib/seo';

/**
 * Home page, PRD §5.1.
 * Copy is lifted from Megan's deck per PRD §2: "Lift Megan's voice and copy
 * directly from the deck wherever possible." Slide references are inline.
 */

const problems = [
  'Feeling overwhelmed by school or responsibilities',
  'Lack of motivation or confidence',
  'Poor time management or organization',
  'Test anxiety or stress',
  'Difficulty navigating friendships or communication',
  'Preparing for college, careers, or major transitions',
];

const outcomes = [
  'Feel more confident in themselves',
  'Have clear systems for school and responsibilities',
  'Be better equipped to handle stress and challenges',
  'Take ownership of their decisions and future',
];

export function Home() {
  useDocumentMeta({
    title:
      'Your Lane Coaching | Student Success Coaching for Middle School, High School & College',
    description:
      'One-on-one student success coaching for middle school, high school, and college students. Skill-building, confidence, and a clear path forward. Florida-based, virtual nationwide.',
    path: '/',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Your Lane Coaching',
      url: 'https://yourlanecoaching.com',
      email: 'megan@yourlanecoaching.com',
      areaServed: { '@type': 'State', name: 'Florida' },
      founder: { '@type': 'Person', name: 'Megan Lane' },
      priceRange: '$',
      description:
        'Virtual 1:1 student success coaching for middle school, high school, and college students.',
    },
  });

  return (
    <>
      {/* ============================================================
       * Hero, Slide 2 master tagline, restructured as
       *   "rejection stack → big punchline reveal" so the emotional
       *   arc lands rather than reading as a wall of repeated text.
       *   Watercolor wash is a full-bleed atmospheric backdrop, not
       *   a placeholder image box.
       * ============================================================ */}
      <section className="hero-wash relative isolate overflow-hidden">
        <Container className="relative py-24 sm:py-28 lg:py-36">
          <div className="max-w-[58rem]">
            <p className="eyebrow">Student Success Coaching · Florida · Virtual nationwide</p>

            {/* Three "rejections", italic Fraunces, smaller, builds tension. */}
            <p className="rejection mt-10">
              <span>You don&rsquo;t have to compete.</span>
              <span>You don&rsquo;t have to compare.</span>
              <span>You don&rsquo;t have to be defined by your past.</span>
            </p>

            {/* The punchline, the single line that the rejections were
                building toward. Large display Fraunces, ink for the
                first half, sage-deep for the release. */}
            <h1 className="mt-7 font-display text-[clamp(2.125rem,6vw,5.25rem)] font-medium leading-[1.02] tracking-[-0.02em] text-[var(--color-ink)]">
              You can move forward
              <span className="block text-[var(--color-sage-deep)]">
                in your lane.
              </span>
            </h1>

            <p className="lede mt-10 max-w-[34rem]">
              One-on-one student success coaching for middle school, high
              school, and college students. Skill-building, confidence, and
              a clear path forward.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <CTAButton to="/book-consult" size="lg">
                Book a Free Consult
              </CTAButton>
              <CTAButton to="/programs" size="lg" variant="secondary">
                See the programs
              </CTAButton>
            </div>

            <p className="mt-5 text-sm text-[var(--color-ink-soft)]">
              Not sure which program fits?{' '}
              <Link to="/quiz" className="font-semibold text-[var(--color-sage-deep)] underline">
                Take the 2-minute quiz
              </Link>
            </p>
          </div>
        </Container>
      </section>

      {/* ============================================================
       * "Is this you?", Slide 6 problem block
       * ============================================================ */}
      <section className="bg-[var(--color-cream)] py-20 sm:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12">
            <header className="lg:col-span-5">
              <p className="eyebrow">Is this you?</p>
              <h2 className="mt-4">Does your student struggle with&hellip;</h2>
              <p className="lede mt-6 max-w-prose">
                You&rsquo;re not alone, and your student isn&rsquo;t stuck.
              </p>
            </header>

            <ul className="grid gap-3 lg:col-span-7 sm:grid-cols-2">
              {problems.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
                >
                  <span
                    aria-hidden
                    className="mt-1 inline-block size-2 shrink-0 rounded-full bg-[var(--color-tan)]"
                  />
                  <span className="text-[var(--color-ink)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* ============================================================
       * Three programs, Slide 5 framing
       * ============================================================ */}
      <section className="bg-[var(--color-surface)] py-20 sm:py-24">
        <Container>
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="eyebrow">Three signature programs</p>
              <h2 className="mt-3">A program for where your student is, right now.</h2>
            </div>
            <p className="max-w-md text-[var(--color-ink-soft)]">
              Each program is six weeks, one-on-one, and tailored to the
              student. Pricing starts at $597 for founding families.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {programs.map((p) => (
              <ProgramCard key={p.slug} program={p} />
            ))}
          </div>
        </Container>
      </section>

      {/* ============================================================
       * Quiz CTA, "not sure which program fits?"
       * ============================================================ */}
      <section className="py-16 sm:py-20">
        <Container width="narrow">
          <div className="rounded-[var(--radius-lg)] border-2 border-[var(--color-sage-deep)] bg-[var(--color-cream)] p-8 text-center sm:p-12">
            <p className="eyebrow text-[var(--color-sage-deep)]">Not sure which fits?</p>
            <h2 className="mt-3">Take the 2-minute quiz.</h2>
            <p className="lede mt-5 mx-auto max-w-prose">
              Answer a few quick questions about your student and we&rsquo;ll point you to the
              program that fits best. Your result shows up right away and lands in your inbox.
            </p>
            <div className="mt-8 flex justify-center">
              <CTAButton to="/quiz" size="lg">
                Find your fit
              </CTAButton>
            </div>
          </div>
        </Container>
      </section>

      {/* ============================================================
       * Outcomes, Slide 8 "By the end of this program…"
       * ============================================================ */}
      <section className="py-20 sm:py-24">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow">Here&rsquo;s what changes</p>
            <h2 className="mt-3">
              By the end, your student will&hellip;
            </h2>
            <p className="lede mt-6 max-w-prose">
              Skills they&rsquo;ll keep. Habits that hold. Confidence that
              isn&rsquo;t borrowed from a grade or a friend.
            </p>
          </div>
          <ol className="lg:col-span-7 space-y-5">
            {outcomes.map((o, i) => (
              <li
                key={o}
                className="flex items-start gap-5 border-t border-[var(--color-border)] pt-5 first:border-0 first:pt-0"
              >
                <span
                  aria-hidden
                  className="font-display text-2xl text-[var(--color-tan-deep)] tabular-nums"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-lg text-[var(--color-ink)]">{o}</span>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      {/* ============================================================
       * Testimonial slot, PRD §5.1: placeholder, reserve the layout
       * ============================================================ */}
      <section className="bg-[var(--color-surface)] py-20 sm:py-24">
        <Container className="text-center">
          <p className="eyebrow">Words from families</p>
          <h2 className="mt-3">In their own words.</h2>
          <p className="lede mt-5 mx-auto max-w-prose">
            Real notes from families Megan has worked with, shared with
            permission and anonymized.
          </p>
          <div className="mt-12">
            <TestimonialCarousel />
          </div>
        </Container>
      </section>

      {/* ============================================================
       * Brand banner, client-supplied artwork. Full bleed, below the fold,
       * lazy so it stays clear of the hero LCP. Its cream wave bottom edge
       * (#f3f0e6) is within 2/255 of --color-cream, so it dissolves into the
       * section below it.
       * ============================================================ */}
      <BrandBanner />

      {/* ============================================================
       * Founding Families CTA
       * ============================================================ */}
      <section className="py-20 sm:py-24">
        <Container width="narrow">
          <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-cream)] p-10 sm:p-14 text-center grain">
            <span className="inline-block rounded-[var(--radius-pill)] bg-[var(--color-tan)] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-ink)]">
              Founding Families · 5 spots
            </span>
            <h2 className="mt-6">
              First five families: $597 for the full 6-week program.
            </h2>
            <p className="lede mt-5 mx-auto max-w-prose">
              We&rsquo;re accepting the first cohort at the introductory
              founding rate while Megan builds her practice. After the first
              five, the program returns to its standard $750 rate.
            </p>
            <div className="mt-8 flex justify-center">
              <CTAButton to="/book-consult" size="lg">
                Book a Free Consult
              </CTAButton>
            </div>
          </div>
        </Container>
      </section>

      {/* ============================================================
       * Footer CTA band, Slide 8 closing line
       * ============================================================ */}
      <section className="bg-[var(--color-sage-deep)] text-[var(--color-cream)]">
        <Container width="narrow" className="py-20 text-center sm:py-24">
          <p className="font-display text-3xl leading-snug sm:text-4xl">
            Your student already has what they need inside of them.
            <br />
            We simply help them find it, strengthen it, and use it.
          </p>
          <div className="mt-10 flex justify-center">
            <CTAButton to="/book-consult" size="lg" variant="inverse">
              Book a Free Consult
            </CTAButton>
          </div>
        </Container>
      </section>
    </>
  );
}

